import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DropdownInterface } from '../../interfaces/cpe-interfaces.index';

@Component({
  selector: 'app-dropdown-menu',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
  ],
  templateUrl: './dropdown-menu.component.html',
  styleUrl: './dropdown-menu.component.scss',
})
export class DropdownMenuComponent<T> implements OnChanges {
  @Input()
  name = '';
  @Input()
  placeholder = '';
  @Input()
  label = '';
  @Input()
  disabled = false;
  @Input({ required: true })
  suggestions: DropdownInterface[] = [];
  @Input()
  defaultSelectedSuggestion?: DropdownInterface;
  @Input() control: FormControl = new FormControl({
    value: undefined,
    disabled: true,
  });
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() optionsDisabled: T[] = [];
  @Input()
  variant: 'standard' | 'header' | 'mobile' = 'standard';
  @Output()
  selectedValueEvent = new EventEmitter();

  @ViewChild('outerButton')
  outerButton!: ElementRef;

  @ViewChild('suggestionsContainer')
  suggestionsContainer!: ElementRef;

  selectedSuggestion: DropdownInterface | undefined;

  displaySuggestions = false;

  focusedElement?: EventTarget;

  activeSuggestion?: DropdownInterface | undefined;;

  // mobileSubmenu?: DropdownInterface;

  @Output()
  submenuSelected = new EventEmitter<{
    title: string;
    items: { name: string; url: string }[];
  }>();

  @Input() selected = false;

  @Input() dropdownImage?: string;

  private router = inject(Router);

  ngOnChanges() {
    if (this.defaultSelectedSuggestion) {
      this.selectedSuggestion = this.defaultSelectedSuggestion;

      if (this.defaultSelectedSuggestion.name) {
        this.control.setValue(this.defaultSelectedSuggestion.name);
      }
    } else if (this.suggestions[0]?.name) {
      this.selectedSuggestion = this.suggestions[0];
    }
  }

  setActiveSuggestion(item: DropdownInterface) {
    this.activeSuggestion = item;
  }

  onClickSuggestion(clickedSuggestion: DropdownInterface) {
    if (clickedSuggestion) {
      this.selectedSuggestion = clickedSuggestion;
      this.control.setValue(clickedSuggestion.name);
      this.selectedValueEvent.emit(clickedSuggestion.name);

      if (typeof clickedSuggestion.url === 'string') {
        this.router.navigate([clickedSuggestion.url]);
      }
    }

    this.displaySuggestions = false;

    if (this.variant === 'header') {
      this.outerButton?.nativeElement?.focus();
    } else if (this.variant === 'standard') {
      document.getElementById(`dropdown-input-${this.name}-button`)?.focus();
    }
  }

  handleDisplaySuggestions(disabled: boolean) {
    if (disabled) return;

    if (this.suggestions.length) {
      this.displaySuggestions = !this.displaySuggestions;

      if (this.displaySuggestions) {
        this.activeSuggestion = undefined;
        this.control.markAsUntouched();
        if (!this.control.value) {
          this.selectedSuggestion = undefined;
        }

        setTimeout(() => {
          if (this.outerButton && this.suggestionsContainer) {
            const buttonWidth = this.outerButton.nativeElement.offsetWidth;
            const container = this.suggestionsContainer.nativeElement;

            container.style.minWidth = `${buttonWidth}px`;
          }
        }, 0);
      } else {
        this.control.markAllAsTouched();
      }
    }
  }

  onKeyDownSuggestion(event: KeyboardEvent) {
    const srcElement = event.target as HTMLElement;
    const spc = event.key === ' ';
    const enter = event.key === 'Enter';
    const down = event.key === 'ArrowDown';
    const up = event.key === 'ArrowUp';
    const esc = event.key === 'Escape';
    const tab = event.key === 'Tab';
    const firstChild: HTMLElement = srcElement?.parentElement?.parentElement?.firstElementChild
      ?.firstElementChild as HTMLElement;
    const lastChild: HTMLElement = srcElement?.parentElement?.parentElement?.lastElementChild
      ?.lastElementChild as HTMLElement;
    const nextSibling: HTMLElement = srcElement?.parentElement?.nextElementSibling
      ?.firstElementChild as HTMLElement;
    const prevSibling: HTMLElement = srcElement?.parentElement?.previousElementSibling
      ?.firstElementChild as HTMLElement;

    if (tab || enter) {
      return;
    }
    event.preventDefault();
    if (down) {
      if (nextSibling) {
        nextSibling.focus();
      } else {
        firstChild.focus();
      }
    }
    if (up) {
      if (prevSibling) {
        prevSibling.focus();
      } else {
        lastChild.focus();
      }
    }
    if (esc || spc) {
      this.displaySuggestions = false;
      if (this.variant === 'header') {
        this.outerButton?.nativeElement?.focus();
      } else if (this.variant === 'standard') {
        document.getElementById(`dropdown-input-${this.name}-button`)?.focus();
      }
    }
  }

  onKeyDownButton(event: KeyboardEvent) {
    const down = event.key === 'ArrowDown';
    const up = event.key === 'ArrowUp';
    const esc = event.key === 'Escape';
    const spc = event.key === ' ';
    if (down) {
      event.preventDefault();
      this.suggestionsContainer?.nativeElement?.firstElementChild?.firstElementChild?.focus();
    } else if (up) {
      event.preventDefault();
      this.suggestionsContainer?.nativeElement?.lastElementChild?.lastElementChild?.focus();
    } else if (spc) {
      event.preventDefault();
      this.displaySuggestions = !this.displaySuggestions;
    } else if (esc && this.displaySuggestions) {
      this.displaySuggestions = false;
    }
  }


  setFocusedElement(event?: Event) {
    this.focusedElement = event ? (event.target as HTMLElement) : undefined;
  }

  openMobileSubmenu(item: DropdownInterface): void {
    this.submenuSelected.emit({
      title: item.name,
      items: item.subdropdown ?? []
    });
  }

  // closeMobileSubmenu(): void {
  //   this.mobileSubmenu = undefined;
  // }
}
